import './lib/zodConfig' // must stay first: configures Zod before any schema is created
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { Toaster } from 'sonner'
import { AuthProvider } from '@/features/auth'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { NotFoundPage } from '@/features/marketing'
import { routeTree } from './routes/routeTree'
import './lib/i18n'
import './index.css'

// Apply persisted theme synchronously to prevent flash of wrong theme
;(function () {
  try {
    const stored = localStorage.getItem('skillsup-ui')
    const theme = (stored ? JSON.parse(stored)?.state?.theme : null) ?? 'dark'
    if (theme === 'dark') document.documentElement.classList.add('dark')
  } catch {
    document.documentElement.classList.add('dark')
  }
})()

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.05,
  })
}

// Serve cached data for 30s instead of refetching on every mount/focus.
// Mutations still invalidate their keys, so edits show up immediately.
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000 } },
})
// Live-session status is changed server-side (Whereby webhook), so keep it fresh.
for (const key of [['sessions'], ['session'], ['attendees']]) {
  queryClient.setQueryDefaults(key, { staleTime: 0 })
}
const router = createRouter({ 
  routeTree, 
  defaultNotFoundComponent: NotFoundPage,
  scrollRestoration: true 
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
)
